import Vue from "vue";
import { debounce } from "lodash";
import { totalItemsByTable } from "@/api";
import { constants } from "@/utils/constants";
import { mapActions, mapMutations, mapState } from "vuex";
import { paginateTable } from "@/interfaces/Additional.interfaces";
import { fetchData } from "@/interfaces/Tables.interfaces";

export const getDataTables = Vue.extend({
  data: () => {
    return {
      tableSearch: "",
      totalItems: 0,
      loading: false,
      options: {} as any
    };
  },
  computed: {
    ...mapState({
      tablename: "tablename"
    })
  },
  methods: {
    ...mapActions(["getPaginateData"]),
    ...mapMutations(["modifyMultiple"]),

    async getDataFromApi() {
      if (this.loading) return; // Prevent concurrent calls

      // Ensure options are initialized
      if (!this.options || !this.options.page) {
        this.options = {
          page: 1,
          itemsPerPage: 10,
          sortBy: [],
          sortDesc: []
        };
      }

      this.loading = true;
      const { sortBy = [], sortDesc = [] } = this.options || {};

      if (sortBy.length) {
        this.modifyMultiple([
          { property: "sortBy", value: sortBy[0] },
          { property: "sortDesc", value: sortDesc[0] }
        ]);
      }

      try {
        // Trim whitespace so an accidental leading/trailing space on a numeric
        // ID search (e.g. " 377850 ") still matches the row. Backend does
        // CAST(id AS CHAR) LIKE '%search%', which would otherwise silently
        // return zero rows (T2-5696).
        const trimmedSearch =
          typeof this.tableSearch === "string" ? this.tableSearch.trim() : "";
        const hasSearch = trimmedSearch.length > 0;

        // Use search-specific columns if searching, otherwise use all display columns
        const columnKey = hasSearch
          ? this.tablename.toLowerCase() + "TableSearch"
          : this.tablename.toLowerCase() + "Table";

        // Get search columns - only use whitelisted search columns, never fall back to display columns
        const getSearchColumns = () => {
          const searchKey = this.tablename.toLowerCase() + "TableSearch";
          const searchCols = constants.enabledColumns[searchKey];
          if (!searchCols || !Array.isArray(searchCols)) {
            return [];
          }
          
          // For Job table, backend expects columns with j. prefix
          if (this.tablename === "Job") {
            return searchCols.map(col => col.includes(".") ? col : `j.${col}`);
          }
          
          // For Shift table, backend expects s. prefix for Shift columns, j. prefix for Job columns
          if (this.tablename === "Shift") {
            return searchCols.map(col => {
              if (col.includes(".")) return col; // Already prefixed
              // locationId comes from Job table
              if (col === "locationId") return "j.locationId";
              // id and jobId are Shift table columns
              return `s.${col}`;
            });
          }
          
          return searchCols;
        };

        // Always fetch total items when searching to get accurate count across all pages
        // For initial load, only fetch if totalItems is 0
        if (this.totalItems === 0 || hasSearch) {
          const searchCols = hasSearch ? getSearchColumns() : undefined;
          this.totalItems = await totalItemsByTable({
            tableName: this.tablename,
            ...(hasSearch && {
              search: trimmedSearch,
              // Only use whitelisted search columns
              searchColumns: searchCols,
              columnNames:
                constants.enabledColumns[columnKey] ||
                constants.enabledColumns[this.tablename.toLowerCase() + "Table"]
            })
          });
        }

        // Only load the current page - server-side pagination
        const searchCols = hasSearch ? getSearchColumns() : undefined;
        const requestData = {
          tableName: this.tablename,
          limit: this.options.itemsPerPage || 10,
          page: this.options.page || 1,
          columnNames:
            constants.enabledColumns[columnKey] ||
            constants.enabledColumns[this.tablename.toLowerCase() + "Table"],
          ...(hasSearch && {
            search: trimmedSearch,
            // Only use whitelisted search columns
            searchColumns: searchCols
          })
        } as paginateTable;

        await this.getPaginateData(requestData);

        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    }
  },
  watch: {
    options: {
      handler: debounce(function(this: any) {
        // Only fetch if options are properly initialized
        if (this.options && this.options.page && this.options.itemsPerPage) {
          this.getDataFromApi();
        }
      }, 150), // Reduced debounce for better responsiveness
      deep: true,
      immediate: false // Don't trigger on initial mount
    },

    tableSearch: debounce(function(this: any) {
      // Reset total items when searching to get fresh count
      this.totalItems = 0;
      // Reset to page 1 when searching
      if (this.options) {
        this.options.page = 1;
      }
      this.getDataFromApi();
    }, 500) // Reduced debounce for faster search
  },
  mounted() {
    // Initialize data on mount - only load first page
    if (this.options && this.options.page && this.options.itemsPerPage) {
      this.getDataFromApi();
    }
  }
});
